/**
 * Role-based access control configuration.
 * Roles: admin | site_manager | worker | client
 */

// Navigation items per role
export const ROLE_NAV = {
  admin: [
    { label: 'Dashboard', path: '/admin-dashboard', icon: 'LayoutDashboard' },
    { label: 'Sites', path: '/sites', icon: 'Building2' },
    { label: 'Tasks', path: '/task-planner', icon: 'ClipboardList' },
    { label: 'Snags', path: '/snags', icon: 'ClipboardCheck' },
    { label: 'Documents', path: '/document-management', icon: 'FileText' },
    { label: 'Drawings', path: '/drawings-plans', icon: 'PenTool' },
    { label: 'H&S Docs', path: '/rams-management', icon: 'FileCheck' },
    { label: 'Checklists', path: '/checklist-management', icon: 'ListChecks' },
    { label: 'Users & Teams', path: '/workers', icon: 'Users' },
    { label: 'Worker Schedule', path: '/worker-schedule', icon: 'CalendarDays' },
    { label: 'Photos', path: '/site-photo-gallery', icon: 'Images' },
    { label: 'Reports', path: '/report-builder', icon: 'FileBarChart' },
    { label: 'Activity Log', path: '/admin-activity-log', icon: 'Activity' },
    { label: 'Import Data', path: '/csv-import-center', icon: 'FileSpreadsheet' },
    { label: 'Billing', path: '/subscription-management', icon: 'CreditCard' },
  ],
  site_manager: [
    { label: 'Dashboard', path: '/site-manager-dashboard', icon: 'LayoutDashboard' },
    { label: 'Sites', path: '/sites', icon: 'Building2' },
    { label: 'Tasks', path: '/task-planner', icon: 'ClipboardList' },
    { label: 'Snags', path: '/snags', icon: 'ClipboardCheck' },
    { label: 'Documents', path: '/document-management', icon: 'FileText' },
    { label: 'Drawings', path: '/drawings-plans', icon: 'PenTool' },
    { label: 'H&S Docs', path: '/rams-management', icon: 'FileCheck' },
    { label: 'Photos', path: '/site-photo-gallery', icon: 'Images' },
    { label: 'Reports', path: '/report-builder', icon: 'FileBarChart' },
    { label: 'Help', path: '/help', icon: 'HelpCircle' },
  ],
  worker: [
    { label: 'Today', path: '/worker-today', icon: 'Sun' },
    { label: 'My Tasks', path: '/task-completion-workflow', icon: 'ListTodo' },
    { label: 'Report Issue', path: '/snags', icon: 'AlertCircle' },
    { label: 'Documents', path: '/document-management', icon: 'FileText' },
    { label: 'Drawings', path: '/drawings-plans', icon: 'PenTool' },
    { label: 'H&S Docs', path: '/rams-worker-view', icon: 'FileCheck' },
    { label: 'Photos', path: '/site-photo-gallery', icon: 'Images' },
    { label: 'Profile', path: '/profile-settings', icon: 'User' },
  ],
  client: [
    { label: 'Overview', path: '/client-dashboard', icon: 'LayoutDashboard' },
    { label: 'Snags', path: '/snags', icon: 'ClipboardCheck' },
    { label: 'Documents', path: '/document-management', icon: 'FileText' },
    { label: 'Help', path: '/help', icon: 'HelpCircle' },
  ],
};

// Routes each role is allowed to access (path prefixes)
export const ROLE_ALLOWED_ROUTES = {
  admin: [
    '/admin-dashboard',
    '/enhanced-admin-dashboard',
    // Admins reach the site_manager view via direct URL (e.g. for the photo
    // upload flow Heidi uses); not in admin nav, but Routes.jsx already
    // permits admin via RequireAuth allowedRoles. Listed here so this config
    // matches the live guard. [9cBddbMF]
    '/site-manager-dashboard',
    '/sites',
    '/site-dashboard',
    '/site-detail-dashboard',
    '/site-setup-wizard',
    '/workers',
    '/admin-worker-assignment',
    '/worker-schedule',
    '/invite-users',
    '/document-management',
    '/documents',
    '/report-builder',
    '/compliance-report',
    '/budget-report',
    '/notifications-center',
    '/incident-reporting',
    '/daily-site-checklist',
    '/snags',
    '/snags-list-creation',
    '/snag-detail-view',
    '/audits-inspections',
    '/drawings-plans',
    '/drawing-viewer',
    '/drawing-detail',
    '/drawings-gallery',
    '/checklist-management',
    '/camera-first-photo-upload',
    '/task-planner',
    '/task-completion-workflow',
    '/task-analytics-dashboard',
    '/photo-upload-prompt',
    '/site-photo-gallery',
    '/profile-settings',
    '/company-settings',
    '/help',
    '/subscription-management',
    '/invoice-history',
    '/csv-import-center',
    '/rams-management',
    '/site-inductions-management',
    '/toolbox-talks-management',
    '/site-diary-management',
    '/worker-certifications',
    '/compliance-dashboard',
    '/near-misses',
    '/daily-site-report',
    '/admin-activity-log',
  ],
  site_manager: [
    '/site-manager-dashboard',
    '/sites',
    '/site-dashboard',
    '/site-detail-dashboard',
    '/invite-users',
    '/document-management',
    '/documents',
    '/daily-site-checklist',
    '/incident-reporting',
    '/snags',
    '/snags-list-creation',
    '/snag-detail-view',
    '/report-builder',
    '/reports',
    '/compliance-report',
    '/budget-report',
    '/audits-inspections',
    '/drawings-plans',
    '/drawing-viewer',
    '/drawing-detail',
    '/drawings-gallery',
    '/notifications-center',
    '/camera-first-photo-upload',
    '/task-planner',
    '/task-completion-workflow',
    '/task-analytics-dashboard',
    '/photo-upload-prompt',
    '/site-photo-gallery',
    '/profile-settings',
    '/company-settings',
    '/workers',
    '/help',
    '/rams-management',
    '/site-inductions-management',
    '/toolbox-talks-management',
    '/site-diary-management',
    '/worker-certifications',
    '/compliance-dashboard',
    '/near-misses',
    '/daily-site-report',
  ],
  worker: [
    '/worker-today',
    '/worker-dashboard',
    '/home',
    '/tasks',
    '/documents',
    '/profile',
    '/daily-site-checklist',
    '/incident-reporting',
    '/snags',
    '/snag-detail-view',
    '/document-management',
    '/camera-first-photo-upload',
    '/site-dashboard',
    '/task-completion-workflow',
    '/photo-upload-prompt',
    '/profile-settings',
    '/help',
    '/rams-worker-view',
    '/site-inductions-worker-view',
    '/toolbox-talks-worker-view',
    // Drawings — read-only for workers (upload button is gated by role inside
    // the drawings-plans page itself via `canUpload = role IN admin|site_manager`)
    '/drawings-plans',
    '/drawings-gallery',
    '/drawing-viewer',
    '/drawing-detail',
    '/site-photo-gallery',
  ],
  client: [
    '/client-dashboard',
    '/documents',
    '/snags',
    '/snags-list-creation',
    '/help',
    '/profile-settings',
    '/rams-management',
    '/site-inductions-management',
    '/daily-site-report',
  ],
};

// Default dashboard per role
export const ROLE_DEFAULT_DASHBOARD = {
  admin: '/admin-dashboard',
  site_manager: '/site-manager-dashboard',
  worker: '/worker-today',
  client: '/client-dashboard',
};

/**
 * Returns the default dashboard path for a given role.
 */
export const getDefaultDashboard = (role) => {
  return ROLE_DEFAULT_DASHBOARD?.[role] || '/admin-dashboard';
};

/**
 * Returns true if the given role is allowed to access the given path.
 */
export const isRouteAllowed = (role, pathname) => {
  if (!role) return false;
  const allowed = ROLE_ALLOWED_ROUTES?.[role] || [];
  return allowed?.some((prefix) => pathname === prefix || pathname?.startsWith(prefix + '/'));
};

/**
 * Returns the filtered navigation items for a given role.
 */
export const getNavItems = (role) => {
  return ROLE_NAV?.[role] || ROLE_NAV?.['admin'];
};
